import type React from 'react';
import type { MouseEvent } from 'react';

import { IconButton as BaseIconButton, withStyles } from '@material-ui/core';
import type { TablePaginationActionsProps } from '@material-ui/core/TablePagination/TablePaginationActions';
import { css } from 'emotion';

import {
  ChevronLeft,
  ChevronRight,
  FirstPageRounded,
  LastPageRounded,
} from '@material-ui/icons';

import { colors, icons } from '../../styles';

const StyledIconButton = withStyles({
  root: {
    padding: 12,
    borderRadius: 5,
    color: colors.gray.default,
    '&:hover': {
      color: colors.gray.dark,
      backgroundColor: colors.gray.lightest,
    },
  },
  disabled: {
    '&&': {
      color: colors.gray.light,
    },
  },
})(BaseIconButton);

const wrapper = css`
  display: flex;
  margin-left: 20px;
`;

const icon = css`
  && {
    font-size: ${icons.size.small};
  }
`;

export const TablePaginationActions: React.FC<TablePaginationActionsProps> = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
}) => {
  const handleFirstPageButtonClick = (event: MouseEvent<HTMLButtonElement>) =>
    onPageChange(event, 0);

  const handleBackButtonClick = (event: MouseEvent<HTMLButtonElement>) =>
    onPageChange(event, page - 1);

  const handleNextButtonClick = (event: MouseEvent<HTMLButtonElement>) =>
    onPageChange(event, page + 1);

  const handleLastPageButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className={wrapper}>
      <StyledIconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label='Primeira página'
      >
        <FirstPageRounded className={icon} />
      </StyledIconButton>
      <StyledIconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label='Página anterior'
      >
        <ChevronLeft className={icon} />
      </StyledIconButton>
      <StyledIconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='Próxima página'
      >
        <ChevronRight className={icon} />
      </StyledIconButton>
      <StyledIconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='Última página'
      >
        <LastPageRounded className={icon} />
      </StyledIconButton>
    </div>
  );
};
